import Highcharts from 'highcharts';

import formatFloat from 'api/getChartSeries/formatFloat';
import buildFix from 'hooks/useValueFormatters/buildFix';
import ensureNDecimalPlaces from 'hooks/useValueFormatters/ensureNDecimalPlaces';
import numberToCommaString from 'hooks/useValueFormatters/numberToCommaString';
import UNIT_LOCALES from 'propTypes/unitLocales';
import { ChartColors } from 'theme/tokens';
import { InsightsChartSpec } from 'services/streamInsightsQuery';

// Inline charts render at a fixed height inside the chat bubble. Kept generous
// so the plot area isn't squashed when long, rotated category labels claim a
// big share of the vertical space.
export const INSIGHTS_CHART_HEIGHT = 420;

// A pure mirror of useValueFormatters.formatValue (which is a hook and so can't
// be used in this pure module). Insights money is USD/CAD, so we render currency
// with the imperial locale ($/C$) rather than buildFix's metric default (€).
export const formatChartValue = (
  value: number,
  formatting?: InsightsChartSpec['formatting'],
): string => {
  const prefix = buildFix({
    fixType: formatting?.prefix,
    unitsLocale: UNIT_LOCALES.IMPERIAL,
    currencyType: formatting?.currencyType,
  });
  const postfix = buildFix({
    fixType: formatting?.postfix,
    unitsLocale: UNIT_LOCALES.IMPERIAL,
    currencyType: formatting?.currencyType,
  });
  const rounded = formatFloat(value, formatting?.precision);
  const isNegative = value < 0;
  const body = ensureNDecimalPlaces({
    value: numberToCommaString({ value: Math.abs(rounded) }),
    precision: formatting?.precision,
  });

  const base = `${prefix}${body}${postfix}`;
  if (isNegative && formatting?.prefix === 'currency') {
    return `(${base})`;
  }
  if (isNegative) {
    return `-${base}`;
  }
  return base;
};

// Wrap a tooltip body in the visible box. Render the tooltip as HTML (not
// clipped SVG) and draw the box in the HTML itself: that's the only reliable way
// the CSS-variable colours resolve in both light and dark mode (they don't
// resolve on the SVG box). useHTML (not `outside`) keeps it contained within the
// chart — it won't create a body-level container that bleeds over the chat.
export const tooltipHtml = (body: string): string =>
  '<div style="' +
  'background:var(--color-tooltip-bg);' +
  'color:var(--color-tooltip-text);' +
  'border:1px solid var(--color-border-secondary);' +
  'border-radius:8px;padding:6px 10px;font-size:12px;' +
  'white-space:nowrap;">' +
  body +
  '</div>';

export const tooltipShell: Highcharts.TooltipOptions = {
  useHTML: true,
  backgroundColor: 'transparent',
  borderWidth: 0,
  shadow: false,
  padding: 0,
};

// The chrome every inline chart shares: theme colours, no Highcharts branding,
// a transparent fixed-height canvas, a left-aligned title, and a value tooltip.
//
// `usePointName` picks the tooltip label: point-shaped charts (pie, funnel,
// packed bubble) name the point, category charts key off the x-axis category.
export const baseInsightsOptions = ({
  spec,
  chartType,
  usePointName,
  showLegend,
}: {
  spec: InsightsChartSpec;
  chartType: string;
  usePointName: boolean;
  showLegend: boolean;
}): Highcharts.Options => {
  const { formatting } = spec;

  return {
    colors: ChartColors,
    accessibility: { enabled: false },
    credits: { enabled: false },
    chart: {
      type: chartType,
      backgroundColor: 'transparent',
      height: INSIGHTS_CHART_HEIGHT,
      // Always declare polar so switching away from radar clears it. The chart
      // is updated in place via Highcharts' merge-semantics chart.update(), so
      // an omitted key wouldn't reset a previously-set polar:true — the plot
      // would stay a radar spider. radar re-adds polar:true on top of this.
      polar: false,
    },
    title: {
      text: spec.title,
      align: 'left',
      style: { color: 'var(--color-text-primary)', fontSize: '13px' },
    },
    legend: { enabled: showLegend },
    // plotOptions must always be a real object: the highcharts-custom-events
    // plugin (loaded globally) reads chart.options.plotOptions.series on series
    // init, and an explicit `undefined` here would clobber Highcharts' default
    // and crash it. Same reason we never set xAxis/yAxis to undefined.
    plotOptions: {
      series: {
        animation: false,
        ...(spec.stacked && chartType !== 'pie'
          ? { stacking: 'normal' as const }
          : {}),
      },
    },
    tooltip: {
      ...tooltipShell,
      formatter() {
        const formatted = formatChartValue(
          typeof this.y === 'number' ? this.y : 0,
          formatting,
        );
        const label = usePointName ? this.point?.name : this.key;
        return tooltipHtml(label ? `<b>${label}</b>: ${formatted}` : formatted);
      },
    },
  };
};

// The value axis every category chart shares — formatted with the spec's units.
//
// The axis title is intentionally omitted: on these charts it just repeats the
// metric already named in the chart title, and crowds the plot. Setting
// `title.text: undefined` (rather than leaving it off) also suppresses
// Highcharts' yAxis default of "Values". Tick labels are kept.
//
// The XY and cross-tab families deliberately DON'T use this — there the axis
// titles name two different metrics/dimensions and are the only way to tell the
// axes apart, so they set their own.
export const valueAxis = (
  spec: InsightsChartSpec,
): Highcharts.YAxisOptions => ({
  title: { text: undefined },
  labels: {
    formatter() {
      return formatChartValue(Number(this.value), spec.formatting);
    },
  },
});
